import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Wand2, Palette, Grid3X3, CreditCard, Download, Loader2, Upload, Check, X, Link2, FileCheck, Image, Trash2, Plus } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function DesignStudio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('mockups');
  
  // Order/Proposal linking state
  const [orders, setOrders] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(searchParams.get('orderId') || '');
  const [selectedProposalId, setSelectedProposalId] = useState(searchParams.get('proposalId') || '');
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachType, setAttachType] = useState('');
  const [attachData, setAttachData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // AI Design Generation State
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateStyle, setGenerateStyle] = useState('logo');
  const [generateProvider, setGenerateProvider] = useState('openai');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Color Separation State
  const [sepImage, setSepImage] = useState(null);
  const [sepImagePreview, setSepImagePreview] = useState(null);
  const [numColors, setNumColors] = useState(4);
  const [useHalftones, setUseHalftones] = useState(false);
  const [separations, setSeparations] = useState(null);
  const [isSeparating, setIsSeparating] = useState(false);
  
  // DTF Gang Sheet State
  const [dtfImage, setDtfImage] = useState(null);
  const [dtfImagePreview, setDtfImagePreview] = useState(null);
  const [dtfQuantity, setDtfQuantity] = useState(20);
  const [dtfWidth, setDtfWidth] = useState(4);
  const [dtfHeight, setDtfHeight] = useState(5);
  const [gangSheet, setGangSheet] = useState(null);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  
  // Business Card State
  const [cardCompany, setCardCompany] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [cardPhone, setCardPhone] = useState('');
  const [cardEmail, setCardEmail] = useState('');
  const [cardWebsite, setCardWebsite] = useState('');
  const [cardAddress, setCardAddress] = useState('');
  const [cardStyle, setCardStyle] = useState('modern');
  const [cardProvider, setCardProvider] = useState('openai');
  const [businessCard, setBusinessCard] = useState(null);
  const [isCreatingCard, setIsCreatingCard] = useState(false);

  // Custom Mockups State
  const [customMockups, setCustomMockups] = useState([]);
  const [mockupName, setMockupName] = useState('');
  const [mockupCategory, setMockupCategory] = useState('t-shirt');
  const [mockupColor, setMockupColor] = useState('');
  const [mockupFile, setMockupFile] = useState(null);
  const [mockupPreview, setMockupPreview] = useState(null);
  const [isUploadingMockup, setIsUploadingMockup] = useState(false);

  // Design Library State
  const [designs, setDesigns] = useState([]);
  const [designName, setDesignName] = useState('');
  const [designFile, setDesignFile] = useState(null);
  const [designPreview, setDesignPreview] = useState(null);
  const [isUploadingDesign, setIsUploadingDesign] = useState(false);
  const [selectedDesignForProduct, setSelectedDesignForProduct] = useState(null);
  const [productAssignments, setProductAssignments] = useState({});

  // Fetch orders and proposals for linking
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, proposalsRes] = await Promise.all([
          axios.get('/orders'),
          axios.get('/proposals')
        ]);
        setOrders(ordersRes.data || []);
        setProposals(proposalsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch orders/proposals:', error);
      }
    };
    fetchData();
  }, []);

  // Fetch custom mockups and designs
  useEffect(() => {
    const fetchMockupsAndDesigns = async () => {
      try {
        const [mockupsRes, designsRes] = await Promise.all([
          axios.get('/api/design/mockups'),
          axios.get('/api/design/designs')
        ]);
        setCustomMockups(mockupsRes.data || []);
        setDesigns(designsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch mockups/designs:', error);
      }
    };
    fetchMockupsAndDesigns();
  }, []);

  // Handle custom mockup upload
  const handleMockupFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMockupFile(event.target.result.split(',')[1]);
        setMockupPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadCustomMockup = async () => {
    if (!mockupFile || !mockupName.trim()) {
      toast.error('Please provide a name and upload an image');
      return;
    }

    setIsUploadingMockup(true);
    try {
      const response = await axios.post('/api/design/mockups', {
        name: mockupName,
        category: mockupCategory,
        color: mockupColor || null,
        image_base64: mockupFile
      });
      
      setCustomMockups([...customMockups, response.data]);
      setMockupName('');
      setMockupCategory('t-shirt');
      setMockupColor('');
      setMockupFile(null);
      setMockupPreview(null);
      toast.success('Mockup uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload mockup');
    } finally {
      setIsUploadingMockup(false);
    }
  };

  const deleteMockup = async (mockupId) => {
    if (!window.confirm('Delete this mockup?')) return;
    try {
      await axios.delete(`/api/design/mockups/${mockupId}`);
      setCustomMockups(customMockups.filter(m => m.id !== mockupId));
      toast.success('Mockup deleted');
    } catch (error) {
      toast.error('Failed to delete mockup');
    }
  };

  // Handle design upload
  const handleDesignFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setDesignFile(event.target.result.split(',')[1]);
        setDesignPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadDesign = async () => {
    if (!designFile || !designName.trim()) {
      toast.error('Please provide a name and upload a design');
      return;
    }

    setIsUploadingDesign(true);
    try {
      const response = await axios.post('/api/design/designs', {
        name: designName,
        image_base64: designFile
      });
      
      setDesigns([...designs, response.data]);
      setDesignName('');
      setDesignFile(null);
      setDesignPreview(null);
      toast.success('Design uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload design');
    } finally {
      setIsUploadingDesign(false);
    }
  };

  const deleteDesign = async (designId) => {
    if (!window.confirm('Delete this design?')) return;
    try {
      await axios.delete(`/api/design/designs/${designId}`);
      setDesigns(designs.filter(d => d.id !== designId));
      toast.success('Design deleted');
    } catch (error) {
      toast.error('Failed to delete design');
    }
  };

  // Assign design to product
  const assignDesignToProduct = async (productKey, designId) => {
    try {
      setProductAssignments({ ...productAssignments, [productKey]: designId });
      toast.success('Design assigned to product');
    } catch (error) {
      toast.error('Failed to assign design');
    }
  };

  // Handle attaching design to order
  const handleAttachToOrder = async () => {
    if (!selectedOrderId || !attachData) {
      toast.error('Please select an order');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post('/design/save-file', {
        order_id: selectedOrderId,
        file_type: attachType,
        file_base64: attachData.base64,
        file_name: attachData.name,
        metadata: attachData.metadata || {}
      });
      
      toast.success('Design attached to order successfully!');
      setAttachDialogOpen(false);
      setAttachData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach design');
    } finally {
      setIsSaving(false);
    }
  };

  // Open attach dialog
  const openAttachDialog = (type, base64, name, metadata = {}) => {
    setAttachType(type);
    setAttachData({ base64, name, metadata });
    setAttachDialogOpen(true);
  };

  // Generate AI Design
  const handleGenerateDesign = async () => {
    if (!generatePrompt.trim()) {
      toast.error('Please enter a design description');
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await axios.post('/api/design/generate', {
        prompt: generatePrompt,
        style: generateStyle,
        provider: generateProvider
      });
      
      setGeneratedImage(response.data);
      toast.success('Design generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate design');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle image upload for separation
  const handleSepImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setSepImage(base64);
        setSepImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Create Color Separations
  const handleCreateSeparations = async () => {
    if (!sepImage) {
      toast.error('Please upload an image first');
      return;
    }
    
    setIsSeparating(true);
    try {
      const response = await axios.post('/api/design/color-separation', {
        image_base64: sepImage,
        num_colors: numColors,
        use_halftones: useHalftones
      });
      
      setSeparations(response.data);
      toast.success(`Created ${response.data.total_colors} color separations!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create separations');
    } finally {
      setIsSeparating(false);
    }
  };

  // Handle DTF image upload
  const handleDtfImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setDtfImage(base64);
        setDtfImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Create DTF Gang Sheet
  const handleCreateGangSheet = async () => {
    if (!dtfImage) {
      toast.error('Please upload a design first');
      return;
    }
    
    setIsCreatingSheet(true);
    try {
      const response = await axios.post('/api/design/dtf-gang-sheet', {
        design_base64: dtfImage,
        quantity: dtfQuantity,
        design_width_inches: dtfWidth,
        design_height_inches: dtfHeight
      });
      
      setGangSheet(response.data);
      toast.success(`Gang sheet created with ${response.data.layout.quantity_on_this_sheet} designs!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gang sheet');
    } finally {
      setIsCreatingSheet(false);
    }
  };

  // Create Business Card
  const handleCreateBusinessCard = async () => {
    if (!cardCompany || !cardName || !cardPhone || !cardEmail) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setIsCreatingCard(true);
    try {
      const response = await axios.post('/api/design/business-card', {
        company_name: cardCompany,
        person_name: cardName,
        title: cardTitle,
        phone: cardPhone,
        email: cardEmail,
        website: cardWebsite || null,
        address: cardAddress || null,
        style: cardStyle,
        provider: cardProvider
      });
      
      setBusinessCard(response.data);
      toast.success('Business card created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business card');
    } finally {
      setIsCreatingCard(false);
    }
  };

  // Download helper
  const downloadBase64 = (base64, filename, mimeType = 'image/png') => {
    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${base64}`;
    link.download = filename;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Design Studio</h1>
          <p className="text-gray-600 mt-2">Create and prepare designs for printing</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
            <TabsTrigger value="mockups" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Mockups</span>
            </TabsTrigger>
            <TabsTrigger value="designs" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Designs</span>
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              <span className="hidden sm:inline">AI Generate</span>
            </TabsTrigger>
            <TabsTrigger value="separation" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Color Sep</span>
            </TabsTrigger>
            <TabsTrigger value="dtf" className="flex items-center gap-2">
              <Grid3X3 className="h-4 w-4" />
              <span className="hidden sm:inline">DTF Sheet</span>
            </TabsTrigger>
            <TabsTrigger value="cards" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Cards</span>
            </TabsTrigger>
          </TabsList>

          {/* Custom Mockups Tab */}
          <TabsContent value="mockups">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload New Mockup */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-blue-600" />
                    Upload Custom Mockup
                  </CardTitle>
                  <CardDescription>Add your own product mockup templates</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mockup Name *</Label>
                    <Input
                      value={mockupName}
                      onChange={(e) => setMockupName(e.target.value)}
                      placeholder="e.g., Gildan 5000 Black Front"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={mockupCategory} onValueChange={setMockupCategory}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="t-shirt">T-Shirt</SelectItem>
                          <SelectItem value="polo">Polo</SelectItem>
                          <SelectItem value="hoodie">Hoodie</SelectItem>
                          <SelectItem value="long-sleeve">Long Sleeve</SelectItem>
                          <SelectItem value="tank">Tank Top</SelectItem>
                          <SelectItem value="hat">Hat/Cap</SelectItem>
                          <SelectItem value="jacket">Jacket</SelectItem>
                          <SelectItem value="bag">Bag</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Color (optional)</Label>
                      <Input
                        value={mockupColor}
                        onChange={(e) => setMockupColor(e.target.value)}
                        placeholder="e.g., Black, Navy"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Mockup Image</Label>
                    <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                      {mockupPreview ? (
                        <div className="relative">
                          <img src={mockupPreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="absolute top-0 right-0"
                            onClick={() => { setMockupFile(null); setMockupPreview(null); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">Click to upload mockup image</p>
                          <p className="text-xs text-gray-400">PNG or JPG recommended</p>
                          <input type="file" className="hidden" accept="image/*" onChange={handleMockupFileChange} />
                        </label>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={uploadCustomMockup}
                    disabled={isUploadingMockup || !mockupFile || !mockupName}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isUploadingMockup ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Mockup
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Mockup Library */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Your Mockup Library</CardTitle>
                  <CardDescription>{customMockups.length} custom mockups</CardDescription>
                </CardHeader>
                <CardContent>
                  {customMockups.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {customMockups.map((mockup) => (
                        <div 
                          key={mockup.id} 
                          className="relative group border rounded-lg overflow-hidden hover:border-blue-400 transition-colors"
                        >
                          <img
                            src={`data:image/png;base64,${mockup.image_base64}`}
                            alt={mockup.name}
                            className="w-full h-32 object-cover"
                          />
                          <div className="p-2 bg-white">
                            <p className="text-xs font-medium truncate">{mockup.name}</p>
                            <p className="text-xs text-gray-500">{mockup.category} {mockup.color && `• ${mockup.color}`}</p>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                            onClick={() => deleteMockup(mockup.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Image className="h-12 w-12 mx-auto mb-4" />
                      <p>No custom mockups yet</p>
                      <p className="text-sm">Upload your own mockup templates to get started</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Designs Library Tab */}
          <TabsContent value="designs">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload New Design */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-purple-600" />
                    Upload Design
                  </CardTitle>
                  <CardDescription>Add logos and artwork to your library</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Design Name *</Label>
                    <Input
                      value={designName}
                      onChange={(e) => setDesignName(e.target.value)}
                      placeholder="e.g., Team Sosa Logo"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Design File</Label>
                    <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                      {designPreview ? (
                        <div className="relative">
                          <img src={designPreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="absolute top-0 right-0"
                            onClick={() => { setDesignFile(null); setDesignPreview(null); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">Click to upload design</p>
                          <p className="text-xs text-gray-400">PNG with transparency recommended</p>
                          <input type="file" className="hidden" accept="image/*" onChange={handleDesignFileChange} />
                        </label>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={uploadDesign}
                    disabled={isUploadingDesign || !designFile || !designName}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {isUploadingDesign ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Design
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Design Library */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Your Design Library</CardTitle>
                  <CardDescription>{designs.length} designs • Click a design to assign it to products</CardDescription>
                </CardHeader>
                <CardContent>
                  {designs.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {designs.map((design) => (
                        <div 
                          key={design.id} 
                          className={`relative group border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                            selectedDesignForProduct === design.id 
                              ? 'border-purple-500 ring-2 ring-purple-200' 
                              : 'border-gray-200 hover:border-purple-400'
                          }`}
                          onClick={() => setSelectedDesignForProduct(
                            selectedDesignForProduct === design.id ? null : design.id
                          )}
                        >
                          <div className="bg-gray-100 p-4">
                            <img
                              src={`data:image/png;base64,${design.image_base64}`}
                              alt={design.name}
                              className="w-full h-24 object-contain"
                            />
                          </div>
                          <div className="p-2 bg-white">
                            <p className="text-xs font-medium truncate">{design.name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(design.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {selectedDesignForProduct === design.id && (
                            <div className="absolute top-1 left-1 bg-purple-500 text-white rounded-full p-1">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); deleteDesign(design.id); }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Wand2 className="h-12 w-12 mx-auto mb-4" />
                      <p>No designs in your library</p>
                      <p className="text-sm">Upload designs or generate them with AI</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Product Assignment Section */}
            {selectedDesignForProduct && proposals.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Assign Design to Products</CardTitle>
                  <CardDescription>Select a proposal and assign the selected design to specific products</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Select onValueChange={(proposalId) => {
                      const proposal = proposals.find(p => p.id === proposalId);
                      if (proposal && proposal.items) {
                        // Show products from this proposal
                      }
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a proposal..." />
                      </SelectTrigger>
                      <SelectContent>
                        {proposals.slice(0, 20).map(proposal => (
                          <SelectItem key={proposal.id} value={proposal.id}>
                            {proposal.proposal_number} - {proposal.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AI Design Generation */}
          <TabsContent value="generate">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wand2 className="h-5 w-5 text-purple-600" />
                    Generate Design
                  </CardTitle>
                  <CardDescription>Create logos and artwork using AI</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Design Description</Label>
                    <Textarea
                      placeholder="Describe your design... e.g., 'A bold eagle logo with spread wings for a sports team called Thunder Hawks'"
                      value={generatePrompt}
                      onChange={(e) => setGeneratePrompt(e.target.value)}
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Style</Label>
                      <Select value={generateStyle} onValueChange={setGenerateStyle}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="logo">Logo</SelectItem>
                          <SelectItem value="tshirt">T-Shirt Graphic</SelectItem>
                          <SelectItem value="vintage">Vintage/Retro</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="sports">Sports</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>AI Provider</Label>
                      <Select value={generateProvider} onValueChange={setGenerateProvider}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI GPT Image</SelectItem>
                          <SelectItem value="gemini">Gemini Nano Banana</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleGenerateDesign} 
                    disabled={isGenerating}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating... (may take up to 60s)
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-4 w-4 mr-2" />
                        Generate Design
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  {generatedImage ? (
                    <div className="space-y-4">
                      <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
                        <img 
                          src={`data:image/png;base64,${generatedImage.image_base64}`}
                          alt="Generated design"
                          className="max-w-full max-h-[400px] object-contain rounded"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => downloadBase64(generatedImage.image_base64, 'design.png')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PNG
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setSepImage(generatedImage.image_base64);
                            setSepImagePreview(`data:image/png;base64,${generatedImage.image_base64}`);
                            setActiveTab('separation');
                            toast.success('Design sent to Color Separation');
                          }}
                        >
                          <Palette className="h-4 w-4 mr-2" />
                          Color Sep
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setDtfImage(generatedImage.image_base64);
                            setDtfImagePreview(`data:image/png;base64,${generatedImage.image_base64}`);
                            setActiveTab('dtf');
                            toast.success('Design sent to DTF Sheet');
                          }}
                        >
                          <Grid3X3 className="h-4 w-4 mr-2" />
                          DTF Sheet
                        </Button>
                      </div>
                      <Button 
                        variant="default"
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => openAttachDialog('logo', generatedImage.image_base64, `ai_design_${Date.now()}.png`, { provider: generatedImage.provider, prompt: generatePrompt })}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Attach to Order
                      </Button>
                      <p className="text-xs text-gray-500">
                        Provider: {generatedImage.provider} | Model: {generatedImage.model}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                      <Wand2 className="h-12 w-12 mb-4" />
                      <p>Generated design will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Color Separation */}
          <TabsContent value="separation">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5 text-blue-600" />
                    Screen Print Color Separation
                  </CardTitle>
                  <CardDescription>Separate artwork into individual color layers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Upload Design</Label>
                    <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      {sepImagePreview ? (
                        <div className="relative">
                          <img src={sepImagePreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="absolute top-0 right-0"
                            onClick={() => { setSepImage(null); setSepImagePreview(null); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">Click to upload or drag & drop</p>
                          <input type="file" className="hidden" accept="image/*" onChange={handleSepImageUpload} />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Number of Colors</Label>
                      <Select value={numColors.toString()} onValueChange={(v) => setNumColors(parseInt(v))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5, 6].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n} Colors</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2 pt-6">
                      <Switch 
                        id="halftones" 
                        checked={useHalftones}
                        onCheckedChange={setUseHalftones}
                      />
                      <Label htmlFor="halftones">Use Halftones</Label>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateSeparations}
                    disabled={isSeparating || !sepImage}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isSeparating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Separations...
                      </>
                    ) : (
                      <>
                        <Palette className="h-4 w-4 mr-2" />
                        Create Color Separations
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Color Layers</CardTitle>
                </CardHeader>
                <CardContent>
                  {separations ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        {separations.total_colors} colors separated • 
                        Original: {separations.original_size.width}×{separations.original_size.height}px
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {separations.separations.map((sep, idx) => (
                          <div key={idx} className="border rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-6 h-6 rounded border"
                                  style={{ backgroundColor: sep.hex_color }}
                                />
                                <span className="font-medium text-sm">{sep.color_name}</span>
                              </div>
                              <span className="text-xs text-gray-500">{sep.hex_color}</span>
                            </div>
                            <div className="bg-gray-100 rounded p-2">
                              <img 
                                src={`data:image/png;base64,${sep.image_base64}`}
                                alt={`Layer ${sep.layer_number}`}
                                className="max-w-full h-auto"
                              />
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => downloadBase64(sep.image_base64, `separation_${sep.color_name.replace(/\s/g, '_')}.png`)}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          separations.separations.forEach((sep, idx) => {
                            setTimeout(() => {
                              downloadBase64(sep.image_base64, `separation_${idx + 1}_${sep.color_name.replace(/\s/g, '_')}.png`);
                            }, idx * 500);
                          });
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download All Layers
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                      <Palette className="h-12 w-12 mb-4" />
                      <p>Color separations will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* DTF Gang Sheet */}
          <TabsContent value="dtf">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Grid3X3 className="h-5 w-5 text-green-600" />
                    DTF Gang Sheet
                  </CardTitle>
                  <CardDescription>Create print-ready gang sheets (22" × 36")</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Upload Design</Label>
                    <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      {dtfImagePreview ? (
                        <div className="relative">
                          <img src={dtfImagePreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="absolute top-0 right-0"
                            onClick={() => { setDtfImage(null); setDtfImagePreview(null); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">Click to upload or drag & drop</p>
                          <input type="file" className="hidden" accept="image/*" onChange={handleDtfImageUpload} />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Quantity</Label>
                      <Input 
                        type="number" 
                        value={dtfQuantity}
                        onChange={(e) => setDtfQuantity(parseInt(e.target.value) || 1)}
                        min={1}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Width (inches)</Label>
                      <Input 
                        type="number" 
                        value={dtfWidth}
                        onChange={(e) => setDtfWidth(parseFloat(e.target.value) || 1)}
                        min={0.5}
                        step={0.5}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Height (inches)</Label>
                      <Input 
                        type="number" 
                        value={dtfHeight}
                        onChange={(e) => setDtfHeight(parseFloat(e.target.value) || 1)}
                        min={0.5}
                        step={0.5}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateGangSheet}
                    disabled={isCreatingSheet || !dtfImage}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {isCreatingSheet ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Gang Sheet...
                      </>
                    ) : (
                      <>
                        <Grid3X3 className="h-4 w-4 mr-2" />
                        Create Gang Sheet
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Gang Sheet Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  {gangSheet ? (
                    <div className="space-y-4">
                      <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-center">
                        <img 
                          src={`data:image/png;base64,${gangSheet.gang_sheet_base64}`}
                          alt="Gang sheet"
                          className="max-w-full max-h-[400px] object-contain rounded"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-gray-100 rounded p-2">
                          <span className="text-gray-500">Sheet Size:</span>
                          <span className="font-medium ml-1">{gangSheet.sheet_size.width_inches}" × {gangSheet.sheet_size.height_inches}"</span>
                        </div>
                        <div className="bg-gray-100 rounded p-2">
                          <span className="text-gray-500">Design Size:</span>
                          <span className="font-medium ml-1">{gangSheet.design_size.width_inches}" × {gangSheet.design_size.height_inches}"</span>
                        </div>
                        <div className="bg-gray-100 rounded p-2">
                          <span className="text-gray-500">Designs on Sheet:</span>
                          <span className="font-medium ml-1">{gangSheet.layout.quantity_on_this_sheet}</span>
                        </div>
                        <div className="bg-gray-100 rounded p-2">
                          <span className="text-gray-500">Layout:</span>
                          <span className="font-medium ml-1">{gangSheet.layout.designs_per_row} × {gangSheet.layout.designs_per_column}</span>
                        </div>
                      </div>
                      {gangSheet.layout.total_sheets_needed > 1 && (
                        <p className="text-amber-600 text-sm">
                          ⚠️ {gangSheet.layout.total_sheets_needed} sheets needed for {dtfQuantity} designs
                        </p>
                      )}
                      <Button 
                        className="w-full"
                        onClick={() => downloadBase64(gangSheet.gang_sheet_base64, 'dtf_gang_sheet.png')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Gang Sheet (PNG)
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => openAttachDialog('dtf_gang_sheet', gangSheet.gang_sheet_base64, `dtf_gang_sheet_${Date.now()}.png`, gangSheet.layout)}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Attach to Order
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                      <Grid3X3 className="h-12 w-12 mb-4" />
                      <p>Gang sheet preview will appear here</p>
                      <p className="text-xs mt-2">22" × 36" @ 300 DPI with transparent background</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Business Cards */}
          <TabsContent value="cards">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-orange-600" />
                    Business Card Creator
                  </CardTitle>
                  <CardDescription>AI-generated print-ready business cards</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input 
                        value={cardCompany}
                        onChange={(e) => setCardCompany(e.target.value)}
                        placeholder="Stares Group"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Your Name *</Label>
                      <Input 
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        placeholder="John Smith"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input 
                        value={cardTitle}
                        onChange={(e) => setCardTitle(e.target.value)}
                        placeholder="CEO"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input 
                        value={cardPhone}
                        onChange={(e) => setCardPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      value={cardEmail}
                      onChange={(e) => setCardEmail(e.target.value)}
                      placeholder="john@staresgroup.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Website</Label>
                    <Input 
                      value={cardWebsite}
                      onChange={(e) => setCardWebsite(e.target.value)}
                      placeholder="www.staresgroup.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Address</Label>
                    <Textarea 
                      value={cardAddress}
                      onChange={(e) => setCardAddress(e.target.value)}
                      placeholder="123 Main St, City, State 12345"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Style</Label>
                      <Select value={cardStyle} onValueChange={setCardStyle}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="modern">Modern</SelectItem>
                          <SelectItem value="classic">Classic</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="bold">Bold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>AI Provider</Label>
                      <Select value={cardProvider} onValueChange={setCardProvider}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI GPT Image</SelectItem>
                          <SelectItem value="gemini">Gemini Nano Banana</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateBusinessCard}
                    disabled={isCreatingCard}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    {isCreatingCard ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating... (may take up to 60s)
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Create Business Card
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  {businessCard ? (
                    <div className="space-y-4">
                      <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                        <img 
                          src={`data:image/png;base64,${businessCard.card_image_base64}`}
                          alt="Business card"
                          className="max-w-full rounded shadow-lg"
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        <p><strong>Size:</strong> {businessCard.specifications.size}</p>
                        <p><strong>Bleed:</strong> {businessCard.specifications.bleed}</p>
                        <p><strong>DPI:</strong> {businessCard.specifications.dpi}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => downloadBase64(businessCard.card_image_base64, 'business_card.png')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PNG
                        </Button>
                        <Button 
                          className="flex-1"
                          onClick={() => downloadBase64(businessCard.print_ready_pdf_base64, 'business_card_print.pdf', 'application/pdf')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Print-Ready PDF
                        </Button>
                      </div>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => openAttachDialog('business_card', businessCard.card_image_base64, `business_card_${Date.now()}.png`, businessCard.content)}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Attach to Order
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                      <CreditCard className="h-12 w-12 mb-4" />
                      <p>Business card preview will appear here</p>
                      <p className="text-xs mt-2">3.5" × 2" with bleed & crop marks</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Attach to Order Dialog */}
        <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Attach Design to Order
              </DialogTitle>
              <DialogDescription>
                Select an order to attach this {attachType?.replace('_', ' ')} design file.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Select Order</Label>
                <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose an order..." />
                  </SelectTrigger>
                  <SelectContent>
                    {orders.filter(o => o.status !== 'delivered').map(order => (
                      <SelectItem key={order.id} value={order.id}>
                        {order.order_number} - {order.customer_name} ({order.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {orders.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">No active orders found</p>
                )}
              </div>
              
              {attachData && (
                <div className="bg-gray-100 rounded-lg p-3">
                  <p className="text-sm font-medium">File Details:</p>
                  <p className="text-xs text-gray-600">Type: {attachType}</p>
                  <p className="text-xs text-gray-600">Name: {attachData.name}</p>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setAttachDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleAttachToOrder}
                  disabled={!selectedOrderId || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FileCheck className="h-4 w-4 mr-2" />
                      Attach to Order
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
